import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import * as styles from "./styles.module.scss";

const CustomDatePickerIcon = ({ title, ...props }) => (
  <Tooltip title={title} placement="top-end">
    <CalendarMonthOutlinedIcon
      sx={{
        color: "var(--palette-text)",
        width: "19px",
        marginRight: "6px",
        pointerEvents: "auto",
        ...props?.sx,
      }}
      {...props}
    />
  </Tooltip>
);

const UIDateTimePicker = ({
  name,
  value,
  title,
  placeholder,
  onChange,
  onBlur,
  error,
  helperText,
  disabled = false,
  size = "medium",
  sx = {},
  format,
  tooltip = "",
  minDate = "",
  maxDate = "",
  isLocal = true,
  required = false,
  smallPaper = false,
}) => {
  const dateTimeformat = format || "DD-MMM-YYYY hh:mm A";
  const handleOnChange = (e) => {
    if (dateTimeformat) {
      const date = isLocal ? e : e.format(dateTimeformat);
      onChange({ value: date });
      return;
    }
    onChange(e);
  };

  // Parse UTC date string and convert to local time
  const localDateTime = value
    ? isLocal
      ? moment.utc(value).local()
      : moment.utc(value)
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateTimePicker
        format={dateTimeformat}
        views={["year", "month", "day", "hours", "minutes"]}
        slots={{
          openPickerIcon: (props) => (
            <CustomDatePickerIcon title={tooltip} {...props} />
          ),
        }}
        slotProps={{
          popper: {
            className: styles.dateTimepickerPaper,
          },
          textField: {
            onFocus: (e) => {
              // Commit the value when tabbing out
              if (!localDateTime) {
                handleOnChange(moment());
              }
            },
            required: required,
            size: size,
            placeholder: dateTimeformat,
            helperText: error ? helperText || error : "",
            error: !!error,
            InputLabelProps: {
              style: {
                color: error
                  ? "var(--palette-error)"
                  : disabled
                    ? "var(--palette-disabled_button_text)"
                    : "var(--palette-grey_text)",
              },
            },
            InputProps: {
              style: {
                color: "var(--palette-text)",
                background: disabled
                  ? "var(--palette-disabled_button)"
                  : "var(--palette-grid_table)",
              },
            },
            FormHelperTextProps: {
              style: {
                position: "absolute",
                margineTop: 0,
                bottom: "-20px",
                // ...helperTextStyles,
              },
            },
          },
          openPickerButton: {
            sx: {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              justifyContent: "flex-end",
              borderRadius: 0,
            },
          },
          day: {
            sx: {
              color: "var(--palette-text, inherit)",
              "&:hover": {
                backgroundColor:
                  "var(--palette-secondary_button_hover, rgba(0, 0, 0, 0.04))",
              },
              "&.Mui-selected, &.Mui-selected:hover, &:focus.Mui-selected": {
                backgroundColor:
                  "var(--palette-primary_main, var(--palette-theme)) !important",
                color: "#fff !important",
              },
              "&.Mui-disabled": {
                color: "var(--palette-disabled_button_text, #8c8c8c) !important",
              },
            },
          },
          calendarHeader: {
            sx: {
              color: "var(--palette-text) !important",
              "& .MuiPickersCalendarHeader-switchViewButton": {
                color: "var(--palette-text) !important",
              },
            },
          },
          leftArrowIcon: {
            sx: {
              color: "var(--palette-grey_text) !important",
            },
          },
          rightArrowIcon: {
            sx: {
              color: "var(--palette-grey_text) !important",
            },
          },
          popper: {
            className: "custom-date-popper", 
            sx: {
              "& .MuiPickersPopper-paper": smallPaper
                ? {
                    transform: "scale(0.68) !important",
                    transformOrigin: "top left !important",
                  }
                : {},
              "& .MuiDayCalendar-weekDayLabel": {
                color: "var(--palette-grey_text) !important",
              },
              "& .MuiPickersLayout-root ul li.Mui-selected": {
                backgroundColor: "var(--palette-primary_main, var(--palette-theme, var(--blue, #007bff))) !important",
              },
              "& .MuiPickersLayout-root ul li:hover": {
                backgroundColor: "var(--palette-secondary_button_hover)",
              },
              "& .MuiPickersMonth-root .MuiPickersMonth-monthButton.Mui-selected, .MuiPickersYear-root .MuiPickersYear-yearButton.Mui-selected":
                {
                  backgroundColor: "var(--palette-primary_main) !important",
                },
              "& .MuiPickersMonth-root .MuiPickersMonth-monthButton, .MuiPickersYear-root .MuiPickersYear-yearButton":
                {
                  "&:hover": {
                    backgroundColor:
                      "var(--palette-secondary_button_hover) !important",
                  },
                },
            },
          },
        }}
        name={name}
        label={title || ""}
        value={localDateTime}
        onChange={handleOnChange}
        onBlur={onBlur}
        disabled={disabled}
        sx={{ ...sx }}
        minDateTime={moment.utc(minDate).local()}
        maxDateTime={moment.utc(maxDate).local()}
      />
    </LocalizationProvider>
  );
};

export default UIDateTimePicker;
